import { useNavigate } from "react-router-dom";
import { createContext, useContext, useState, useEffect } from "react";

import { IUser } from "@/types";
import {
  clearBackendSession,
  getBackendCurrentUser,
  getStoredAccessTokenExpiresAt,
  getStoredRefreshToken,
  refreshBackendSession,
} from "@/lib/backend/api";
import { isUserBanned } from "@/lib/appwrite/userViolationAPI";
import { useToast } from "@/components/ui/use-toast";
import { useSignOutAccount } from "@/lib/react-query/queries";
import { buildSignInRedirect } from "@/lib/auth/redirect";
import { getKYCStatus } from "@/lib/appwrite/api";

export const INITIAL_USER = {
  id: "",
  name: "",
  username: "",
  emailOrPhone: "",
  imageUrl: "",
  bio: "",
  acceptedSLA: false,
  isSeller: false,
  isSupportAgent: false,
};

const INITIAL_STATE = {
  user: INITIAL_USER,
  isLoading: false,
  isAuthenticated: false,
  setUser: () => {},
  setIsAuthenticated: () => {},
  checkAuthUser: async () => false as boolean,
  isPublicRoute: () => false,
  requiresAuth: () => {},
};

// Define public routes that don't require authentication
export const PUBLIC_ROUTES = [
  '/',
  '/explore',
  '/live-stream',
  '/dealers',
  '/posts/:id', // View individual posts
  '/profile/:id', // View public profiles
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/terms-conditions',
  '/privacy-policy',
  '/user-guide',
  '/contact-us'
];

// Define private routes that require authentication
export const PRIVATE_ROUTES = [
  '/messages',
  '/create-post',
  '/saved',
  '/bizz',
  '/trustscore-dashboard',
  '/profile/edit',
  '/settings',
  '/notifications'
];

type IContextType = {
  user: IUser;
  isLoading: boolean;
  setUser: React.Dispatch<React.SetStateAction<IUser>>;
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  checkAuthUser: () => Promise<boolean>;
  isPublicRoute: (path: string) => boolean;
  requiresAuth: (path: string) => void;
};

const AuthContext = createContext<IContextType>(INITIAL_STATE);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<IUser>(INITIAL_USER);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start with true for initial load
  const { mutate: signOut } = useSignOutAccount();
  const { toast } = useToast();

  const clearSession = () => {
    clearBackendSession();
    setUser(INITIAL_USER);
    setIsAuthenticated(false);
  };

  // Check if a route is public (doesn't require auth)
  const isPublicRoute = (path: string): boolean => {
    return PUBLIC_ROUTES.some(route => {
      // Handle exact matches
      if (route === path) return true;
      
      // Handle parameterized routes (e.g., /posts/:id)
      if (route.includes(':')) {
        const routePattern = route.replace(/:\w+/g, '[^/]+');
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(path);
      }
      
      return false;
    });
  };

  // Redirect to login if route requires auth
  const requiresAuth = (path: string) => {
    if (!isPublicRoute(path) && !isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please sign in to access this feature",
        variant: "default"
      });
      navigate(buildSignInRedirect(path));
    }
  };

  const checkAuthUser = async () => {
    setIsLoading(true);
    try {
      const currentAccount = await getBackendCurrentUser();
      
      if (currentAccount) {
        const userId = currentAccount.id ?? currentAccount.$id;
        let userIsBanned = false;
        try {
          userIsBanned = await isUserBanned(userId);
        } catch (error) {
          // A moderation lookup must not prevent a verified backend session from starting.
          console.warn("Unable to check account moderation status:", error);
        }
        if (userIsBanned) {
          toast({
            title: "Account Suspended",
            description: "Your account is under ban due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
            variant: "destructive"
          });
          signOut();
          setIsAuthenticated(false);
          setUser(INITIAL_USER);
          navigate("/sign-in?banned=true");
          return false;
        }

        let isSeller = false;
        try {
          const kyc = await getKYCStatus(userId);
          isSeller = Boolean(kyc.panVerified && kyc.bankVerified);
        } catch (error) {
          console.warn("Unable to load KYC status:", error);
        }

        setUser({
          id: userId,
          name: currentAccount.name,
          username: currentAccount.username,
          emailOrPhone: currentAccount.emailOrPhone,
          imageUrl: currentAccount.imageUrl ?? "",
          bio: currentAccount.bio ?? "",
          acceptedSLA: currentAccount.acceptedSla ?? currentAccount.acceptedSLA ?? false,
          isSeller,
          isSupportAgent: Boolean(currentAccount.isSupportAgent)
        });
        setIsAuthenticated(true);
        return true;
      } else {
        clearSession();
        return false;
      }
    } catch (error) {
      console.error("Auth check error:", error);
      // Preserve refresh credentials during transient network or deployment errors.
      // A future request, focus event, or scheduled refresh can then recover the session.
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize authentication on app start
  useEffect(() => {
    checkAuthUser();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRefresh = () => {
      const expiresAt = Date.parse(getStoredAccessTokenExpiresAt() ?? "");
      const delay = Number.isFinite(expiresAt)
        ? Math.max(1_000, expiresAt - Date.now() - 60_000)
        : 14 * 60_000;

      refreshTimer = setTimeout(async () => {
        const refreshed = await refreshBackendSession();
        if (!refreshed && !getStoredRefreshToken()) {
          clearSession();
          return;
        }
        scheduleRefresh();
      }, delay);
    };

    const refreshOnFocus = () => {
      const expiresAt = Date.parse(getStoredAccessTokenExpiresAt() ?? "");
      if (!Number.isFinite(expiresAt) || expiresAt - Date.now() <= 60_000) {
        void refreshBackendSession();
      }
    };

    scheduleRefresh();
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [isAuthenticated]);

  const value = {
    user,
    setUser,
    isLoading,
    isAuthenticated,
    setIsAuthenticated,
    checkAuthUser,
    isPublicRoute,
    requiresAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useUserContext = () => useContext(AuthContext);
